import { useEffect, useMemo, useState } from 'react';
import {
  Search,
  ChevronDown,
  ChevronRight,
  BookOpen,
  FileText,
  Loader2,
  ShieldCheck,
  Clock3,
  Layers3,
  Info,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { helpMinRolePresentation, sortHelpCategories } from '@/lib/help-structure';
import { cn } from '@/lib/utils';
import { getHashPath, getHashSearchParams } from '@/lib/hash-route';
import type { ApiResponse, HelpArticle } from '@/types';

interface HelpCategory {
  id: string;
  label: string;
  articles: HelpArticle[];
}
function toCategoryId(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function formatInlineBold(line: string) {
  const parts: React.ReactNode[] = [];
  let rest = line;
  let idx = 0;

  while (rest.includes('**')) {
    const start = rest.indexOf('**');
    const end = rest.indexOf('**', start + 2);
    if (end === -1) break;
    if (start > 0) parts.push(rest.slice(0, start));
    parts.push(<strong key={idx++}>{rest.slice(start + 2, end)}</strong>);
    rest = rest.slice(end + 2);
  }

  if (rest) parts.push(rest);
  return parts.length > 0 ? parts : line;
}

function getExcerpt(content: string) {
  const firstParagraph = content
    .split('\n\n')
    .map((part) => part.trim())
    .find(Boolean);

  if (!firstParagraph) return 'Keine Vorschau verfügbar.';

  const normalized = firstParagraph.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  return normalized.length > 110 ? `${normalized.slice(0, 107)}...` : normalized;
}

function getReadingTime(content: string) {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 180));
}

function formatUpdatedAt(value?: string) {
  if (!value) return 'Unbekannt';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unbekannt';

  return new Intl.DateTimeFormat('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function HelpPage() {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<{ category: string; article: HelpArticle } | null>(null);

  useEffect(() => {
    const loadArticles = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get<ApiResponse<HelpArticle[]>>('/help');
        if (!response.success || !response.data) {
          setArticles([]);
          setError(response.error || 'Die Hilfe konnte nicht geladen werden.');
          return;
        }

        setArticles(response.data);
      } catch (err) {
        setArticles([]);
        setError(err instanceof Error ? err.message : 'Die Hilfe konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    loadArticles();
  }, []);

  const toggleCategory = (id: string) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const categories = useMemo<HelpCategory[]>(() => {
    const grouped = new Map<string, HelpArticle[]>();

    for (const article of articles) {
      const key = article.category?.trim() || 'Allgemein';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(article);
    }

    const unsorted = Array.from(grouped.entries()).map(([label, items]) => ({
      id: toCategoryId(label),
      label,
      articles: [...items].sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title, 'de-CH')),
    }));
    return sortHelpCategories(unsorted);
  }, [articles]);

  const filteredCategories = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return categories;

    return categories
      .map((cat) => ({
        ...cat,
        articles: cat.articles.filter(
          (article) =>
            article.title.toLowerCase().includes(needle) ||
            article.content.toLowerCase().includes(needle) ||
            cat.label.toLowerCase().includes(needle)
        ),
      }))
      .filter((cat) => cat.articles.length > 0);
  }, [categories, search]);

  const filteredArticleCount = useMemo(
    () => filteredCategories.reduce((sum, category) => sum + category.articles.length, 0),
    [filteredCategories]
  );

  useEffect(() => {
    if (categories.length === 0) return;

    const syncFromHash = () => {
      if (getHashPath() !== '/help') return;

      const slug = getHashSearchParams().get('article');
      if (slug) {
        for (const cat of categories) {
          const art = cat.articles.find((a) => a.slug === slug);
          if (art) {
            setExpanded((e) => ({ ...e, [cat.id]: true }));
            setSelectedArticle({ category: cat.id, article: art });
            return;
          }
        }
      }

      const firstCategory = categories[0];
      const firstArticle = firstCategory?.articles[0];
      if (firstCategory && firstArticle) {
        setExpanded({ [firstCategory.id]: true });
        setSelectedArticle({ category: firstCategory.id, article: firstArticle });
      }
    };

    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [categories]);

  const renderContent = (content: string) => {
    return content.split('\n\n').map((block, i) => {
      const trimmed = block.trim();
      if (!trimmed) return null;

      const lines = trimmed.split('\n').map((line) => line.trim()).filter(Boolean);
      const numberedList = lines.every((line) => /^\d+\.\s+/.test(line));
      const bulletList = lines.every((line) => /^[-*]\s+/.test(line));

      if (trimmed.startsWith('**') && trimmed.endsWith('**') && !trimmed.slice(2, -2).includes('**')) {
        return (
          <div
            key={i}
            className="mt-8 rounded-2xl border border-border/80 bg-background px-4 py-3 font-semibold text-foreground dark:border-border dark:bg-card/70"
          >
            {trimmed.slice(2, -2)}
          </div>
        );
      }

      if (numberedList) {
        return (
          <ol key={i} className="mt-3 list-decimal space-y-2 pl-5 text-muted">
            {lines.map((line, index) => (
              <li key={index}>{formatInlineBold(line.replace(/^\d+\.\s+/, ''))}</li>
            ))}
          </ol>
        );
      }

      if (bulletList) {
        return (
          <ul key={i} className="mt-3 list-disc space-y-2 pl-5 text-muted">
            {lines.map((line, index) => (
              <li key={index}>{formatInlineBold(line.replace(/^[-*]\s+/, ''))}</li>
            ))}
          </ul>
        );
      }

      return (
        <p key={i} className="mt-5 text-[15px] leading-8 text-muted lg:text-base">
          {lines.map((line, index) => (
            <span key={index}>
              {formatInlineBold(line)}
              {index < lines.length - 1 ? <br /> : null}
            </span>
          ))}
        </p>
      );
    });
  };

  const rolePresentation = selectedArticle
    ? helpMinRolePresentation(selectedArticle.article.min_role)
    : null;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="w-full space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Hilfe</h1>
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            placeholder="Suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 pl-9"
          />
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
          <p className="inline-flex items-center gap-1 text-xs text-muted">
            <Layers3 className="h-3 w-3" /> Kategorien
          </p>
          <p className="text-lg font-semibold text-foreground">{categories.length}</p>
        </div>
        <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
          <p className="inline-flex items-center gap-1 text-xs text-muted">
            <BookOpen className="h-3 w-3" /> Artikel
          </p>
          <p className="text-lg font-semibold text-foreground">{articles.length}</p>
        </div>
        <div className="rounded-lg border border-border bg-background p-3 dark:bg-card/60">
          <p className="inline-flex items-center gap-1 text-xs text-muted">
            <Search className="h-3 w-3" /> Treffer
          </p>
          <p className="text-lg font-semibold text-foreground">{filteredArticleCount}</p>
        </div>
      </div>

      <div className="grid items-start gap-8 xl:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <Card className="overflow-hidden xl:sticky xl:top-4">
          <CardHeader className="border-b border-border pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="h-4 w-4" />
              Navigation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-18rem)] min-h-[420px] px-4 py-4">
              {loading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-muted">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Hilfe wird geladen...
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-5 text-sm text-muted">
                  Keine Treffer für deine Suche.
                </div>
              ) : (
                <ul className="space-y-3">
                  {filteredCategories.map((cat) => (
                    <li key={cat.id} className="rounded-2xl border border-border/70 bg-background/70 p-2 dark:border-border dark:bg-card/50">
                      <button
                        type="button"
                        className={cn(
                          'flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors',
                          'hover:bg-card'
                        )}
                        onClick={() => toggleCategory(cat.id)}
                      >
                        <div className="min-w-0 pr-2">
                          <div className="text-sm font-semibold leading-snug text-foreground">{cat.label.replace(/^\d{1,2}\s+/, '')}</div>
                          <div className="text-xs text-muted">{cat.articles.length} Artikel</div>
                        </div>
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-card text-muted shadow-sm dark:bg-background">
                          {expanded[cat.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </button>
                      {expanded[cat.id] === true && (
                        <ul className="mt-2 space-y-2">
                          {cat.articles.map((art) => {
                            const isSelected =
                              selectedArticle?.article.id === art.id && selectedArticle?.category === cat.id;

                            return (
                              <li key={art.id}>
                                <button
                                  type="button"
                                  className={cn(
                                    'w-full rounded-2xl border px-3 py-3 text-left transition-all',
                                    isSelected
                                      ? 'border-border bg-card-hover shadow-sm dark:bg-primary-hover/40'
                                      : 'border-transparent bg-card/80 hover:border-border hover:bg-card dark:bg-background/70 dark:hover:bg-background'
                                  )}
                                  onClick={() => {
                                    setSelectedArticle({ category: cat.id, article: art });
                                    const next = `#/help?article=${encodeURIComponent(art.slug)}`;
                                    if (window.location.hash !== next) {
                                      window.location.hash = next;
                                    }
                                  }}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={cn(
                                      'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl',
                                      isSelected
                                        ? 'bg-card-hover text-foreground dark:bg-primary-hover/50 dark:text-primary-foreground'
                                        : 'bg-card-hover text-muted'
                                    )}>
                                      <FileText className="h-4 w-4" />
                                    </div>
                                    <div className="min-w-0">
                                      <div className={cn(
                                        'text-sm font-semibold leading-5',
                                        isSelected ? 'text-foreground dark:text-primary-foreground' : 'text-foreground'
                                      )}>
                                        {art.title}
                                      </div>
                                      <p className="mt-1 text-xs leading-5 text-muted">
                                        {getExcerpt(art.content || art.content_md || '')}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex min-h-[600px] flex-col items-center justify-center py-16 text-center">
                <Loader2 className="h-14 w-14 animate-spin text-muted" />
                <p className="mt-4 text-muted">Ausführliche Hilfetexte werden geladen.</p>
              </div>
            ) : error ? (
              <div className="flex min-h-[600px] flex-col items-center justify-center py-16 text-center">
                <ShieldCheck className="h-14 w-14 text-muted" />
                <p className="mt-4 max-w-xl text-muted">{error}</p>
                <p className="mt-2 text-sm text-muted">
                  Die Hilfe wird aus der Datenbank geladen. Bitte API und Berechtigung `help:view` prüfen.
                </p>
              </div>
            ) : selectedArticle ? (
              <>
                <div className="border-b border-border px-6 py-6 lg:px-8">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="rounded-full px-3 py-1">
                      {selectedArticle.article.category.replace(/^\d{1,2}\s+/, '')}
                    </Badge>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="cursor-default gap-1 rounded-full px-3 py-1">
                          {rolePresentation?.badge}
                          <Info className="h-3 w-3 opacity-70" aria-hidden />
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-sm">
                        {rolePresentation?.description}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <h2 className="mt-4 max-w-4xl text-3xl font-bold tracking-tight text-foreground">
                    {selectedArticle.article.title}
                  </h2>
                  <p className="mt-3 max-w-3xl text-sm leading-7 text-muted">
                    {getExcerpt(selectedArticle.article.content || selectedArticle.article.content_md || '')}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3 text-sm text-muted">
                    <div className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1.5 shadow-sm">
                      <Clock3 className="h-4 w-4" />
                      ca. {getReadingTime(selectedArticle.article.content || selectedArticle.article.content_md || '')} Min. Lesezeit
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1.5 shadow-sm">
                      <Layers3 className="h-4 w-4" />
                      Aktualisiert am {formatUpdatedAt(selectedArticle.article.updated_at)}
                    </div>
                  </div>
                </div>

                <div className="px-6 py-6 lg:px-8 lg:py-8">
                  <Separator />
                  <div className="mt-6 max-w-4xl">
                    {renderContent(selectedArticle.article.content || selectedArticle.article.content_md || '')}
                  </div>
                </div>
              </>
            ) : (
              <div className="flex min-h-[600px] flex-col items-center justify-center py-16 text-center">
                <BookOpen className="h-14 w-14 text-muted" />
                <p className="mt-4 text-muted">Wähle links einen Artikel aus der Navigation.</p>
                <p className="mt-1 text-sm text-muted">Oder nutze die Suche, um direkt ein Thema zu finden.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </TooltipProvider>
  );
}
